import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, LogOut, User, Mail, Phone, Star, Award, Calendar, MapPin, Clock, Euro } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    
    if (!token) {
      toast.error('Veuillez vous connecter');
      navigate('/client/login');
      return;
    }

    fetchCustomerData(token);
  }, [navigate]);

  const fetchCustomerData = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/customers/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomer(data);
        setBookings(data.bookings || []);
      } else {
        toast.error('Session expirée');
        handleLogout();
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customerData');
    toast.success('Déconnexion réussie');
    navigate('/');
  };

  const getLoyaltyProgress = () => {
    if (!customer) return { points: 0, nextReward: 5, percentage: 0 };
    const points = customer.loyaltyPoints || 0;
    const nextReward = 5 - (points % 5);
    const percentage = ((points % 5) / 5) * 100;
    return { points, nextReward, percentage };
  };

  const getStatusBadge = () => {
    if (!customer) return null;
    if (customer.isVIP) {
      return (
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold rounded-full">
          <Star className="w-5 h-5 fill-current" />
          CLIENT VIP
        </div>
      );
    }
    return (
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-700 text-gray-300 font-medium rounded-full">
        <User className="w-5 h-5" />
        Client Standard
      </div>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-500 border-yellow-500',
      confirmed: 'bg-blue-500/20 text-blue-500 border-blue-500',
      completed: 'bg-green-500/20 text-green-500 border-green-500',
      rejected: 'bg-red-500/20 text-red-500 border-red-500'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-500 border-gray-500';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      completed: 'Terminée',
      rejected: 'Refusée'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!customer) {
    return null;
  }

  const loyalty = getLoyaltyProgress();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Mon Espace Client
            </h1>
            <p className="text-gray-400">Bienvenue, {customer.name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all duration-300"
          >
            <LogOut className="w-5 h-5" />
            Déconnexion
          </button>
        </div>

        {/* Customer Info Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Profile Card */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Mon Profil</h2>
              {getStatusBadge()}
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-gray-300">
                <User className="w-5 h-5 text-amber-500" />
                <span>{customer.name}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <Mail className="w-5 h-5 text-amber-500" />
                <span className="text-sm">{customer.email}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <Phone className="w-5 h-5 text-amber-500" />
                <span>{customer.phone}</span>
              </div>
            </div>
          </div>

          {/* Loyalty Card */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-2 mb-4">
              <Award className="w-6 h-6 text-amber-500" />
              <h2 className="text-xl font-bold text-white">Programme Fidélité</h2>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm text-gray-400 mb-2">
                  <span>Courses effectuées</span>
                  <span className="text-amber-500 font-bold">{loyalty.points} / 5</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div
                    className="bg-gradient-to-r from-amber-500 to-yellow-500 h-3 rounded-full transition-all duration-500"
                    style={{ width: `${loyalty.percentage}%` }}
                  />
                </div>
              </div>
              <p className="text-sm text-gray-300">
                {loyalty.nextReward === 5 ? (
                  <span className="text-amber-500 font-medium">
                    Effectuez votre première course pour commencer !
                  </span>
                ) : loyalty.nextReward === 0 ? (
                  <span className="text-green-500 font-medium">
                    🎉 Vous avez droit à 10% de réduction sur votre prochaine course !
                  </span>
                ) : (
                  <span>
                    Plus que <span className="text-amber-500 font-bold">{loyalty.nextReward}</span> course
                    {loyalty.nextReward > 1 ? 's' : ''} pour votre réduction de 10% !
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Stats Card */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Mes Statistiques</h2>
            <div className="space-y-4">
              <div>
                <p className="text-gray-400 text-sm mb-1">Total réservations</p>
                <p className="text-3xl font-bold text-amber-500">{customer.totalBookings || 0}</p>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-1">Total dépensé</p>
                <p className="text-2xl font-bold text-white">{(customer.totalSpent || 0).toFixed(2)} €</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bookings History */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl p-6 border border-gray-700">
          <h2 className="text-2xl font-bold text-white mb-6">Historique des Réservations</h2>
          
          {bookings.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg mb-4">Aucune réservation pour le moment</p>
              <button
                onClick={() => navigate('/#booking')}
                className="px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold rounded-lg transition-all duration-300"
              >
                Réserver maintenant
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div
                  key={booking.id}
                  className="bg-gray-900 rounded-lg p-6 border border-gray-700 hover:border-amber-500/50 transition-all duration-300"
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-white">Réservation #{booking.id}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking.status)}`}>
                          {getStatusLabel(booking.status)}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm">Créée le {formatDate(booking.createdAt)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-amber-500">{booking.estimatedPrice} €</p>
                      <p className="text-sm text-gray-400">{booking.vehicle}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-green-500 mt-1" />
                      <div>
                        <p className="text-xs text-gray-400">Prise en charge</p>
                        <p className="text-white">{booking.pickup}</p>
                      </div>
                    </div>
                    {booking.dropoff && (
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-red-500 mt-1" />
                        <div>
                          <p className="text-xs text-gray-400">Destination</p>
                          <p className="text-white">{booking.dropoff}</p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-amber-500" />
                      <div>
                        <p className="text-xs text-gray-400">Date</p>
                        <p className="text-white">{booking.date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <div>
                        <p className="text-xs text-gray-400">Heure</p>
                        <p className="text-white">{booking.time}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
